import { useEffect, useRef, useState } from "react";
import { itemKey } from "./config";

const FALLBACK_POSITION = Object.freeze({ x: 12, y: 12 });
const TOOLTIP_WIDTH = 336;
const TOOLTIP_EDGE = 8;
const TOOLTIP_VERTICAL_SPACE = 250;
export const HOVER_DELAY_MS = 750;

function tooltipPosition(chart, event) {
  const chartBox = chart?.getBoundingClientRect();
  const targetBox = event.currentTarget.getBoundingClientRect();
  if (!chartBox) return FALLBACK_POSITION;

  const pointerX = Number.isFinite(event.clientX) && event.clientX > 0
    ? event.clientX
    : targetBox.left + Math.min(targetBox.width / 2, 220);
  const pointerY = Number.isFinite(event.clientY) && event.clientY > 0
    ? event.clientY
    : targetBox.top + Math.min(targetBox.height, 24);
  const width = Math.min(TOOLTIP_WIDTH, Math.max(chartBox.width - 16, 0));
  const relativeY = pointerY - chartBox.top;
  const y = relativeY > chartBox.height - TOOLTIP_VERTICAL_SPACE
    ? relativeY - 220
    : relativeY + 14;

  return {
    x: Math.max(TOOLTIP_EDGE, Math.min(
      pointerX - chartBox.left + 12,
      chartBox.width - width - TOOLTIP_EDGE
    )),
    y: Math.max(TOOLTIP_EDGE, y)
  };
}

export function useTimelineTooltip(chartRef, resetKey) {
  const [tooltip, setTooltip] = useState(null);
  const focusedItemRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const tooltipRef = useRef(null);

  const cancelHover = () => {
    window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = null;
  };

  useEffect(() => {
    tooltipRef.current = tooltip;
  }, [tooltip]);

  useEffect(() => {
    cancelHover();
    focusedItemRef.current = null;
    setTooltip(null);
    return cancelHover;
  }, [resetKey]);

  const show = (item, event, pinned = false) => {
    cancelHover();
    setTooltip({ item, ...tooltipPosition(chartRef.current, event), pinned });
  };
  const scheduleHover = (item, event) => {
    if (tooltipRef.current?.pinned || focusedItemRef.current) return;

    const position = tooltipPosition(chartRef.current, event);
    const alreadyShown = itemKey(tooltipRef.current?.item || {}) === itemKey(item);
    cancelHover();
    if (alreadyShown) {
      setTooltip({ item, ...position, pinned: false });
      return;
    }

    setTooltip(null);
    hoverTimerRef.current = window.setTimeout(() => {
      setTooltip({ item, ...position, pinned: false });
      hoverTimerRef.current = null;
    }, HOVER_DELAY_MS);
  };
  const clear = () => {
    cancelHover();
    setTooltip(null);
  };
  const handlersFor = (item) => ({
    "aria-describedby": tooltip?.item && itemKey(tooltip.item) === itemKey(item)
      ? "history-chart-tooltip"
      : undefined,
    onMouseEnter: (event) => scheduleHover(item, event),
    onMouseMove: (event) => scheduleHover(item, event),
    onMouseLeave: () => {
      cancelHover();
      setTooltip((current) => current?.pinned ? current : null);
    },
    onFocus: (event) => {
      focusedItemRef.current = itemKey(item);
      show(item, event);
    },
    onBlur: () => {
      focusedItemRef.current = null;
      setTooltip((current) => current?.pinned ? current : null);
    },
    onPointerDown: (event) => {
      if (event.pointerType === "mouse") return;
      const isPinned = tooltip?.pinned && itemKey(tooltip.item) === itemKey(item);
      if (!isPinned) {
        event.preventDefault();
        show(item, event, true);
      }
    }
  });

  return { clear, handlersFor, tooltip };
}
